import { useMemo } from "react";

import TriangleUp from "@/assets/icons/triangle-up.svg";
import { BACKEND_MENTION_TIMING_ADJUSTMENT } from "@/constants";
import { useMentionCard } from "@/containers/MentionCard/context";
import { useLocalAudio } from "@/containers/MentionCard/LocalAudioContext";
import { TimeCluster } from "@/containers/MentionCard/utils/clusterTimes";
import { AudioMentionV4 } from "@/types/MentionCard";
import { cx, durationFromSeconds } from "@/utils";
import Tooltip from "@components/Tooltip";
import Typography from "@components/Typography";

import styles from "./Timeline.module.css";

type TimelineMarkerProps = {
  mention: AudioMentionV4;
  active: boolean;
  onClick: () => void;
} & React.HTMLProps<HTMLDivElement>;

const TimelineMarker: React.FunctionComponent<TimelineMarkerProps> = ({
  mention,
  active,
  hidden,
  onClick,
  ...props
}: TimelineMarkerProps) => {
  const { playing, audio } = useLocalAudio();
  const { sentimentActive } = useMentionCard();
  return (
    <Tooltip content={durationFromSeconds(mention.context_start_timing)}>
      <div
        {...props}
        style={{
          left: `${
            ((mention.context_start_timing +
              BACKEND_MENTION_TIMING_ADJUSTMENT) /
              mention.total_length) *
            100
          }%`,
        }}
        className={cx(styles.marker, active ? styles.active : styles.inactive, {
          [styles.hidden]: hidden,
        })}
        onClick={(e) => {
          // play/seek only when an audio player exists and is currently playing
          if (playing && audio) {
            audio.play(mention.context_start_timing);
          }
          onClick(e); // always notify parent so UI updates
        }}
      >
        <span
          className={cx(styles.markerInner, {
            [styles.positive]:
              sentimentActive && mention.sentiment === "positive",
            [styles.neutral]:
              sentimentActive && mention.sentiment === "neutral",
            [styles.negative]:
              sentimentActive && mention.sentiment === "negative",
          })}
        >
          <TriangleUp stroke="currentColor" />
        </span>
      </div>
    </Tooltip>
  );
};

type TimelineProps = {
  visible: AudioMentionV4;
  onMarkerClick: (s: AudioMentionV4) => void;
};

const createCluster = (clusters: TimeCluster[], length_seconds: number) => {
  const clusterSizes: Array<{ width: number; left: number }> = [];
  clusters.forEach(([start, end]) => {
    // never mind the the loners right now.
    if (end !== undefined && start !== undefined) {
      const chunkWidth =
        (Math.min(end, length_seconds) - start) / length_seconds;
      const chunkStart = start / length_seconds;

      clusterSizes.push({
        width: chunkWidth * 100,
        left: chunkStart * 100,
      });
    }
  });
  return clusterSizes;
};

const Timeline = ({ visible, onMarkerClick }: TimelineProps) => {
  const {
    clusters,
    audioMentions,
    removedMentionIds,
    episode: { length_seconds },
  } = useMentionCard();
  const { currentTime } = useLocalAudio({ currentTime: true });

  const clusterSizes = useMemo(
    () => createCluster(clusters, length_seconds),
    [clusters, length_seconds]
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <Typography block variant="small" className={styles.timestamp}>
          {durationFromSeconds(
            currentTime || audioMentions?.[0].context_start_timing || 0
          )}
        </Typography>
        <div className={styles.timeline}>
          {clusterSizes.map((c, i) => (
            <div
              key={i}
              className={styles.cluster}
              style={{ left: `${c.left}%`, width: `${c.width}%` }}
            />
          ))}
          <div className={styles.markers}>
            {audioMentions.map((mention) => (
              <TimelineMarker
                data-testid="mention-marker"
                mention={mention}
                data-mention-id={mention.id}
                key={mention.id}
                active={visible?.id === mention.id}
                hidden={removedMentionIds.includes(mention.id)}
                onClick={() => {
                  onMarkerClick(mention);
                }}
              />
            ))}
          </div>
        </div>
        <Typography
          block
          variant="small"
          className={cx(styles.timestamp, styles.end)}
        >
          {durationFromSeconds(length_seconds) || "unknown"}
        </Typography>
      </div>
    </div>
  );
};

export default Timeline;
